import { Form, Input, Modal, Select } from "antd";
import { AppButton } from "components/button/AppButton";
import { generalValidationRules } from "utils/formHelpers/validations";
import { openNotification } from "utils/notification";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { useAddRole } from "../hooks/useAddRole";
import {
  AuthorizationGuard,
  CreateRoleRequest,
} from "../types";
import { useQueryClient } from "react-query";
import { QUERY_KEY_FOR_ROLES } from "../hooks/useGetRoles";
import { useGetPermissions } from "../hooks/useGetPermissions";
import { getAuthorizationErrorMessage } from "../authorizationError";

interface AddRoleProps {
  open: boolean;
  guardName: AuthorizationGuard;
  handleClose: () => void;
}

interface AddRoleFormValues {
  name: string;
  permission_ids?: number[];
}

export const AddRole = ({ open, guardName, handleClose }: AddRoleProps) => {
  const [form] = Form.useForm();
  const { token } = useGetUserInfo();
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useAddRole();
  const { data: permissions, isLoading: permissionsLoading } =
    useGetPermissions({ guardName });

  const handleFormSubmit = (values: AddRoleFormValues) => {
    const props: CreateRoleRequest = {
      token,
      name: values.name,
      guard_name: guardName,
      permission_ids: values.permission_ids,
    };
    mutate(props, {
      onError: (error) => {
        openNotification({
          title: "Error",
          state: "error",
          description: getAuthorizationErrorMessage(
            error,
            "Could not create the role"
          ),
          duration: 6.0,
        });
      },
      onSuccess: () => {
        openNotification({
          title: "Success",
          state: "success",
          description: "Added Successfully",
          duration: 4.5,
        });
        form.resetFields();
        queryClient.invalidateQueries([QUERY_KEY_FOR_ROLES, guardName]);
        handleClose();
      },
    });
  };

  return (
    <Modal
      style={{ top: 20 }}
      footer={false}
      open={open}
      onCancel={handleClose}
      title={`Add new ${guardName === "admins" ? "Admin" : "User"} Role`}
      destroyOnHidden
    >
      <Form layout="vertical" requiredMark="optional" className="mt-5"
        onFinish={handleFormSubmit}
        form={form}
      >
    
        <Form.Item
            name="name"
            label="Role Name"
            rules={generalValidationRules}
        >
            <Input placeholder="Please enter role name" />
        </Form.Item>

        <Form.Item
          name="permission_ids"
          label="Permissions (optional)"
        >
          <Select
            mode="multiple"
            loading={permissionsLoading}
            placeholder="Select permissions"
            allowClear
            optionFilterProp="label"
            options={permissions?.data.map((permission) => ({
              value: permission.id,
              label: permission.name,
            }))}
          />
        </Form.Item>

        <AppButton label="Submit" type="submit" isLoading={isLoading}  />
      </Form>
    </Modal>
  );
};